/**
 * @file: useConversationScroll.ts
 *
 * Sole responsibility: the message list's scroll POSITION and its scroll
 * affordances (jump-to-bottom pill + unread count, jump-to-quoted). It is
 * driven entirely by bus intents it SUBSCRIBES to — it calls no one and no
 * one calls it:
 *
 *   - `messageInserted$` / `messagesResynced$` — follow-or-count.
 *   - `scrollToLoadedMessage$` — the message is now loaded; scroll to it.
 *
 * `isAtBottom$`, the follow-or-count policy, the pill's grace window, and the
 * layout defer are private. It reads the provider-owned `messages$` only to
 * resolve a message id to its row index.
 */

import { MessageListRef } from '@euler/app/flows/chat/components/conversation/messageListShared';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { ChatMsg } from '@euler/model/chat';
import { idEquals } from '@euler/utils';
import { useCreation } from '@euler/utils/hooks';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

interface ConversationScrollDeps {
  /** Provider-owned source of truth — read to resolve an id to a row index. */
  messages$: BehaviorSubject<ChatMsg[]>;
  // ── bus intents this hook handles
  messageInserted$: Observable<ChatMsg>;
  messagesResynced$: Observable<void>;
  scrollToLoadedMessage$: Observable<string>;
}

export interface ConversationScroll {
  listRef: RefObject<MessageListRef>;
  onScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onScrollToIndexFailed: (info: { index: number }) => void;
  /** Smooth-scroll to the newest message (also clears the unread counter). */
  scrollToBottom: () => void;
  // ── read-only view state
  showPill$: BehaviorSubject<boolean>;
  newMessageCount$: BehaviorSubject<number>;
  inputBarHeight$: BehaviorSubject<number>;
  highlightedMessageId$: BehaviorSubject<string | null>;
}

/** Defer a post-insert scroll one layout tick so the new row is measured. */
const SCROLL_AFTER_INSERT_MS = 60;

export function useConversationScroll(
  deps: ConversationScrollDeps,
): ConversationScroll {
  const {
    messages$,
    messageInserted$,
    messagesResynced$,
    scrollToLoadedMessage$,
  } = deps;
  const { me } = useChatSession();

  const listRef = useRef<MessageListRef>(null);
  const isAtBottom$ = useCreation(() => new BehaviorSubject(true), []);
  const newMessageCount$ = useCreation(() => new BehaviorSubject(0), []);
  const inputBarHeight$ = useCreation(() => new BehaviorSubject(0), []);
  const highlightedMessageId$ = useCreation(
    () => new BehaviorSubject<string | null>(null),
    [],
  );
  const showPill$ = useCreation(() => new BehaviorSubject(false), []);

  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      // Inverted list: the visual bottom (newest) is contentOffset.y ≈ 0.
      // Treat "within 40px of offset 0" as at-bottom (smooths fractional
      // layout math on web).
      const atBottom = e.nativeEvent.contentOffset.y < 40;
      if (atBottom !== isAtBottom$.getValue()) isAtBottom$.next(atBottom);
      if (atBottom && newMessageCount$.getValue() > 0) newMessageCount$.next(0);
    },
    [isAtBottom$, newMessageCount$],
  );

  const onScrollToIndexFailed = useCallback((info: { index: number }) => {
    // Quote-jump target not yet measured — retry shortly.
    setTimeout(() => {
      listRef.current?.scrollToIndex({
        index: info.index,
        animated: true,
        viewPosition: 0.5,
      });
    }, 300);
  }, []);

  const scrollToBottom = useCallback(() => {
    listRef.current?.scrollToOffset({ offset: 0, animated: true });
    // Commanding a scroll to the bottom means we intend to be there.
    if (!isAtBottom$.getValue()) isAtBottom$.next(true);
    if (newMessageCount$.getValue() > 0) newMessageCount$.next(0);
  }, [isAtBottom$, newMessageCount$]);

  // Follow a newly-inserted message: own message (or while at the bottom) →
  // follow it down; an incoming message while scrolled up → bump the pill.
  useEffect(() => {
    const sub = messageInserted$.subscribe(msg => {
      const isOwn =
        msg.kind === 'local' || idEquals(msg.authorId, me.platformUserId);
      if (isOwn || isAtBottom$.getValue()) {
        setTimeout(() => scrollToBottom(), SCROLL_AFTER_INSERT_MS);
      } else {
        newMessageCount$.next(newMessageCount$.getValue() + 1);
      }
    });
    return () => sub.unsubscribe();
  }, [messageInserted$, me, isAtBottom$, newMessageCount$, scrollToBottom]);

  // Reconnect resync backfilled messages — follow down only if at the bottom.
  useEffect(() => {
    const sub = messagesResynced$.subscribe(() => {
      if (isAtBottom$.getValue()) {
        setTimeout(() => scrollToBottom(), SCROLL_AFTER_INSERT_MS);
      }
    });
    return () => sub.unsubscribe();
  }, [messagesResynced$, isAtBottom$, scrollToBottom]);

  // A quoted message has been paged in — scroll to it + highlight.
  useEffect(() => {
    const sub = scrollToLoadedMessage$.subscribe(messageId => {
      const idx = messages$
        .getValue()
        .findIndex(m => m.kind === 'remote' && m.messageId === messageId);
      if (idx < 0) return;
      listRef.current?.scrollToIndex({
        index: idx,
        animated: true,
        viewPosition: 0.5,
      });
      highlightedMessageId$.next(messageId);
      setTimeout(() => highlightedMessageId$.next(null), 1500);
    });
    return () => sub.unsubscribe();
  }, [scrollToLoadedMessage$, messages$, highlightedMessageId$]);

  // Pill visible while NOT at bottom, after a 350ms grace window; returning to
  // the bottom hides it instantly.
  useEffect(() => {
    const sub = isAtBottom$
      .pipe(
        distinctUntilChanged(),
        switchMap(atBottom =>
          atBottom ? of(false) : timer(350).pipe(map(() => true)),
        ),
      )
      .subscribe(visible => showPill$.next(visible));
    return () => sub.unsubscribe();
  }, [isAtBottom$, showPill$]);

  return useMemo(
    () => ({
      listRef,
      onScroll,
      onScrollToIndexFailed,
      scrollToBottom,
      showPill$,
      newMessageCount$,
      inputBarHeight$,
      highlightedMessageId$,
    }),
    [
      onScroll,
      onScrollToIndexFailed,
      scrollToBottom,
      showPill$,
      newMessageCount$,
      inputBarHeight$,
      highlightedMessageId$,
    ],
  );
}
